import GraphHeader from "@/components/GraphHeader";
import Bar from "@/components/Nivo/Bar";
import Choropleth from "@/components/Nivo/Choropleth";
import Heatmap from "@/components/Nivo/Heatmap";
import HorizontalBar from "@/components/Nivo/HorizontalBar";
import Line from "@/components/Nivo/Line";
import Pie from "@/components/Nivo/Pie";
import RadialBar from "@/components/Nivo/RadialBar";
import ScatterPlot from "@/components/Nivo/ScatterPlot";
import Column from "@/components/Ui/Column";
import Row from "@/components/Ui/Row";
import {
  GraphToggleOption,
  StatsApiResponseTypes,
  StatsResponse,
} from "@/utils/graphHelpers";
import { useCallback, useEffect } from "react";
import { twMerge } from "tailwind-merge";

type Props = {
  statsResponse?: StatsResponse | any;
  className?: string;
  singleDate?: boolean;
  startDate?: Date;
  endDate?: Date;
  isLegacyGraph?: boolean;
  gradient?: boolean;
  renderedState?: [boolean, (rendered: boolean) => void];
  equalHeight?: boolean;
  toggleOptions?: GraphToggleOption[];
  onGraphToggle?: (option: GraphToggleOption) => void;
};

const Nivo = ({
  statsResponse = {},
  className = "",
  singleDate = false,
  startDate,
  endDate,
  isLegacyGraph = false,
  gradient = false,
  renderedState = [true, () => {}],
  equalHeight = false,
  toggleOptions,
  onGraphToggle,
}: Props) => {
  const [rendered, setRendered] = renderedState;
  const {
    data,
    title,
    description,
    type,
    indexBy,
    cardValue,
    cardValueUnit,
    cardValueType,
    graphConfig,
    queryUrl,
  } = statsResponse;

  const isCurrency = cardValueType === "currency";

  const missingData =
    StatsApiResponseTypes.LINE.includes(type) && data && !data[0]?.data?.length;
  const graphHeaderValue =
    missingData || (!cardValue && cardValue !== 0)
      ? undefined
      : cardValue / (isCurrency ? 100 : 1);

  const fullyRendered = useCallback(() => setRendered(true), [setRendered]);

  useEffect(() => {
    if (missingData) fullyRendered();
  }, [missingData, fullyRendered]);

  if (type === StatsApiResponseTypes.NUMBER) {
    fullyRendered();
  }

  return (
    <div
      className={twMerge(
        "flex h-full w-full flex-1 flex-col",
        className,
        gradient && "from-primary to-card bg-linear-[165deg] to-50%",
      )}
    >
      <Column
        className={twMerge(
          "h-full justify-between gap-6 p-6 opacity-0 transition-opacity duration-300",
          rendered && "opacity-100",
        )}
      >
        {title && (
          <GraphHeader
            title={title}
            description={description}
            unit={cardValueUnit}
            gradient={gradient}
            equalHeight={equalHeight}
            queryUrl={queryUrl}
            toggleOptions={toggleOptions}
            onGraphToggle={onGraphToggle}
            {...(missingData ? {} : { value: graphHeaderValue })}
          />
        )}

        {missingData ? (
          <Row className="h-full w-full text-center">
            There is not enough
            <br />
            data to draw this graph.
          </Row>
        ) : (
          <>
            {[StatsApiResponseTypes.LINE].includes(type) && (
              <Line
                data={data}
                isLegacyGraph={isLegacyGraph}
                singleDate={singleDate}
                isCurrency={isCurrency}
                graphConfig={graphConfig}
                fullyRendered={fullyRendered}
              />
            )}
            {type === StatsApiResponseTypes.RADIAL_BAR && (
              <RadialBar data={data} fullyRendered={fullyRendered} />
            )}
            {type === StatsApiResponseTypes.HEATMAP && (
              <Heatmap data={data} fullyRendered={fullyRendered} />
            )}
            {[
              StatsApiResponseTypes.CATEGORY,
              StatsApiResponseTypes.PIE,
            ].includes(type) && (
              <Pie data={data} fullyRendered={fullyRendered} />
            )}
            {type === StatsApiResponseTypes.BAR && (
              <Bar
                data={data}
                indexBy={indexBy}
                isCurrency={isCurrency}
                startDate={startDate}
                endDate={endDate}
                graphConfig={graphConfig}
                fullyRendered={fullyRendered}
              />
            )}
            {type === StatsApiResponseTypes.HORIZONTAL_BAR && (
              <HorizontalBar
                data={data}
                indexBy={indexBy}
                isCurrency={isCurrency}
                startDate={startDate}
                endDate={endDate}
                graphConfig={graphConfig}
                fullyRendered={fullyRendered}
              />
            )}
            {type === StatsApiResponseTypes.CHOROPLETH && (
              <Choropleth data={data} fullyRendered={fullyRendered} />
            )}
            {type === StatsApiResponseTypes.SCATTER_PLOT && (
              <ScatterPlot data={data} fullyRendered={fullyRendered} />
            )}
          </>
        )}
      </Column>
    </div>
  );
};

export default Nivo;
